import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import mt5ServersBundled from '../../data/mt5-servers.json';


const STEPS = [
  { id: 1, label: 'Your Login' },
  { id: 2, label: 'MT5 Account' },
  { id: 3, label: 'Review' },
];

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: '', username: '', password: '',
    mt5AccountNumber: '', mt5Server: '', mt5Password: '',
  });
  const [broker, setBroker]   = useState('');
  const [manual, setManual]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Live broker/server list from /api/brokers (mirrored from the copy-trading
  // harvester onto a shared mount), with the bundled JSON as the offline/empty
  // fallback so the picker always has something to show.
  const [mt5Servers, setMt5Servers] = useState(mt5ServersBundled);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get('/brokers')
      .then(res => {
        const list = res.data?.servers;
        if (!alive || !Array.isArray(list) || !list.length) return;
        // MERGE harvested servers with the bundled list (don't replace) so we
        // never show fewer than the curated fallback; harvested wins on conflict.
        const byServer = new Map();
        for (const s of mt5ServersBundled) byServer.set(s.server, s);
        for (const s of list) byServer.set(s.server, s);
        setMt5Servers([...byServer.values()]);
      })
      .catch(() => { /* keep bundled fallback */ });
    return () => { alive = false; };
  }, []);

  const brokers = useMemo(
    () => Array.from(new Set(mt5Servers.map(s => s.broker))).sort(),
    [mt5Servers]
  );
  const serversForBroker = useMemo(
    () => mt5Servers.filter(s => s.broker === broker),
    [mt5Servers, broker]
  );

  function handleChange(e) {
    const { name, type, value, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleBrokerChange(e) {
    const next = e.target.value;
    setBroker(next);
    setForm(prev => ({ ...prev, mt5Server: '' }));
  }

  function toggleManual() {
    setManual(prev => !prev);
    setBroker('');
    setForm(prev => ({ ...prev, mt5Server: '' }));
  }

  function validateStep(s) {
    setError('');
    if (s === 1) {
      if (!form.email) { setError('Email is required.'); return false; }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) { setError('Enter a valid email.'); return false; }
      if (!form.username) { setError('Username is required.'); return false; }
      if (!/^[a-z0-9_.-]{3,24}$/i.test(form.username)) {
        setError('Username must be 3–24 chars: letters, numbers, _ . -');
        return false;
      }
      if (!form.password || form.password.length < 6) {
        setError('Password must be at least 6 characters.'); return false;
      }
    }
    if (s === 2) {
      if (!form.mt5AccountNumber) { setError('MT5 account number is required.'); return false; }
      if (!form.mt5Server) { setError('MT5 server is required.'); return false; }
      if (!form.mt5Password) { setError('MT5 password is required.'); return false; }
    }
    return true;
  }

  function next() {
    if (!validateStep(step)) return;
    setStep(s => Math.min(STEPS.length, s + 1));
  }
  function back() {
    setError('');
    setStep(s => Math.max(1, s - 1));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!validateStep(2)) { setStep(2); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/register', form);
      setSuccess('Registration successful! Awaiting admin verification.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="wrapper wrapper-full-page">
      <div className="full-page register-page" data-color="black">
        <div className="content">
          <div className="container">
            <div className="col-lg-7 col-md-10 col-sm-12 ml-auto mr-auto">
              <div className="card card-register">
                <div className="card-header text-center">
                  <h3 className="header text-white mb-1">Create Account</h3>
                  <p className="text-white-50 small mb-3">
                    Step {step} of {STEPS.length} — {STEPS[step-1].label}
                  </p>
                  <div className="wizard-stepper">
                    {STEPS.map((s, i) => (
                      <React.Fragment key={s.id}>
                        <div className={
                          'wizard-step' +
                          (step === s.id ? ' is-current' : '') +
                          (step > s.id   ? ' is-done'    : '')
                        }>
                          <div className="wizard-step-bubble">
                            {step > s.id ? '✓' : s.id}
                          </div>
                          <div className="wizard-step-label">{s.label}</div>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`wizard-step-line ${step > s.id ? 'is-done' : ''}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="card-body">
                  {error   && <div className="alert alert-danger">{error}</div>}
                  {success && <div className="alert alert-success">{success}</div>}

                  <form onSubmit={handleSubmit}>

                    {step === 1 && (
                      <>
                        <div className="alert alert-info" style={{ fontSize: 13 }}>
                          <strong>These are NOT your MT5 details.</strong> This is
                          the email &amp; password you'll use to sign in to this
                          platform. You'll enter your MT5 broker credentials in the
                          next step.
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">Email</label>
                          <input type="email" name="email" className="form-control"
                            placeholder="you@example.com" autoFocus
                            value={form.email} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">Username</label>
                          <input
                            type="text" name="username" className="form-control"
                            placeholder="e.g. og_trader"
                            autoCapitalize="off" autoCorrect="off" spellCheck="false"
                            value={form.username}
                            onChange={e => setForm(f => ({
                              ...f,
                              username: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ''),
                            }))}
                            required
                          />
                          <small className="text-white-50">
                            Your display name on the platform — not your email.
                            3–24 chars, lowercase letters, numbers, _ . -
                          </small>
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">Password</label>
                          <input type="password" name="password" className="form-control"
                            placeholder="Min 6 characters"
                            value={form.password} onChange={handleChange} required />
                          <small className="text-white-50">
                            Used only to sign in to this platform.
                          </small>
                        </div>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <div className="form-group">
                          <label className="text-white-50">MT5 Account Number</label>
                          <input type="text" name="mt5AccountNumber" className="form-control"
                            placeholder="e.g. 6047164" autoFocus
                            value={form.mt5AccountNumber} onChange={handleChange} />
                        </div>
                        {!manual && (
                          <>
                            <div className="form-group">
                              <label className="text-white-50">Broker</label>
                              <select className="form-control" value={broker} onChange={handleBrokerChange}>
                                <option value="">Select your broker…</option>
                                {brokers.map(b => (
                                  <option key={b} value={b}>{b}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="text-white-50">MT5 Server</label>
                              <select name="mt5Server" className="form-control"
                                value={form.mt5Server} onChange={handleChange} disabled={!broker}>
                                <option value="">{broker ? 'Select server…' : 'Choose broker first'}</option>
                                {serversForBroker.map(s => (
                                  <option key={s.server} value={s.server}>
                                    {s.server} ({s.type}){s.note ? ` — ${s.note}` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}
                        {manual && (
                          <div className="form-group">
                            <label className="text-white-50">MT5 Server (manual)</label>
                            <input type="text" name="mt5Server" className="form-control"
                              placeholder="e.g. ICMarketsSC-Live20"
                              value={form.mt5Server} onChange={handleChange} />
                            <small className="text-white-50">
                              Find this on your broker's MT5 setup page.
                            </small>
                          </div>
                        )}
                        <div className="form-group">
                          <button type="button" className="btn btn-link btn-sm text-info p-0"
                            onClick={toggleManual}>
                            {manual ? '← Pick from broker list' : "My broker isn't listed →"}
                          </button>
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">MT5 Password</label>
                          <input type="password" name="mt5Password" className="form-control"
                            placeholder="MT5 account password"
                            value={form.mt5Password} onChange={handleChange} />
                          <small className="text-white-50">
                            Use your master password — EAs need full trading rights,
                            so investor (read-only) passwords won't work.
                          </small>
                        </div>
                      </>
                    )}

                    {step === 3 && (
                      <div className="wizard-review">
                        <ReviewRow label="Email"         value={form.email}            onEdit={() => setStep(1)} />
                        <ReviewRow label="Username"      value={form.username}         onEdit={() => setStep(1)} />
                        <ReviewRow label="Password"      value={'•'.repeat(Math.min(form.password.length, 10))} onEdit={() => setStep(1)} />
                        <ReviewRow label="MT5 Account"   value={form.mt5AccountNumber} onEdit={() => setStep(2)} />
                        <ReviewRow label="MT5 Server"    value={form.mt5Server}        onEdit={() => setStep(2)} />
                        <ReviewRow label="MT5 Password"  value={'•'.repeat(Math.min(form.mt5Password.length, 10))} onEdit={() => setStep(2)} />
                        <div className="text-white-50 small mt-3">
                          After registration an admin verifies your account before your MT5 container is started.
                        </div>
                      </div>
                    )}

                    <div className="wizard-nav">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={back}
                        disabled={step === 1 || loading}
                      >
                        ← Back
                      </button>
                      {step < STEPS.length ? (
                        <button type="button" className="btn btn-primary" onClick={next}>
                          Next →
                        </button>
                      ) : (
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                          {loading ? 'Creating account...' : 'Create Account'}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
                <div className="card-footer text-center">
                  <Link to="/login" className="text-white">Already have an account? Sign In</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, onEdit }) {
  return (
    <div className="review-row">
      <div className="review-row-label">{label}</div>
      <div className="review-row-value">{value || <span className="text-muted">—</span>}</div>
      <button type="button" className="btn btn-link btn-sm p-0 text-info" onClick={onEdit}>Edit</button>
    </div>
  );
}
